/**
 * The screen flow (08 §1) in one place: every `Nav` entry builds its screen fresh and shows it.
 * This is the only module that imports the screens, so they never import each other.
 *
 *   title -> new operation -> form company -> THEATRE MAP -> briefing -> ranger select -> loadout -> fly
 *   title -> classic roster -> mission picker -> briefing -> loadout -> fly
 *   title -> practice picker -> briefing -> fly
 *   title -> daily drop -> (briefing ->) fly
 *   title -> personnel -> one man's dossier and his service file
 *   title -> options -> controls
 *   title -> options -> cloud save (-> title, once a save from another device has been loaded)
 *   (mission) -> aftermath -> theatre map | operation end | classic roster | practice picker | daily drop
 */

import { aftermathScreen } from './aftermath/aftermath';
import { briefingScreen } from './briefing/briefing';
import { classicMissionScreen } from './classic/missionPicker';
import { classicRosterScreen } from './classic/roster';
import { cloudScreen } from './cloud/cloudScreen';
import type { Ctx, Nav, Screen } from './context';
import { controlsScreen } from './controls/controlsScreen';
import { changesScreen } from './changes/changesScreen';
import { creditsScreen } from './credits';
import { dailyDropScreen } from './daily/dailyDrop';
import { formCompanyScreen } from './formCompany';
import { hallScreen } from './hall';
import { loadoutScreen } from './loadout/loadout';
import { memorialScreen } from './memorial';
import { newOperationScreen } from './newOperation';
import { operationEndScreen } from './operationEnd';
import { optionsScreen } from './options';
import { personnelScreen } from './personnel';
import { practiceScreen } from './practice';
import { rangerSelectScreen } from './rangerSelect';
import { recordsScreen } from './records';
import { theatreMapScreen } from './theatre/theatreMap';
import { titleScreen } from './title';

/**
 * `ctx` is a getter because the context holds this `Nav`: each needs the other to exist. `show`
 * is handed a factory so the router can retire the old screen before the new one is built.
 */
export function createNav(ctx: () => Ctx, show: (make: () => Screen) => void): Nav {
  return {
    title: () => show(() => titleScreen(ctx())),
    options: () => show(() => optionsScreen(ctx())),
    credits: () => show(() => creditsScreen(ctx())),
    changes: () => show(() => changesScreen(ctx())),
    newOperation: () => show(() => newOperationScreen(ctx())),
    formCompany: () => show(() => formCompanyScreen(ctx())),
    theatre: () => show(() => theatreMapScreen(ctx())),
    operationEnd: () => show(() => operationEndScreen(ctx())),
    briefing: (plan) => show(() => briefingScreen(ctx(), plan)),
    rangerSelect: (plan) => show(() => rangerSelectScreen(ctx(), plan)),
    loadout: (plan) => show(() => loadoutScreen(ctx(), plan)),
    aftermath: (input) => show(() => aftermathScreen(ctx(), input)),
    hall: () => show(() => hallScreen(ctx())),
    memorial: () => show(() => memorialScreen(ctx())),
    personnel: () => show(() => personnelScreen(ctx())),
    classic: () => show(() => classicRosterScreen(ctx())),
    classicMissions: (rangerId) => show(() => classicMissionScreen(ctx(), rangerId)),
    practice: () => show(() => practiceScreen(ctx())),
    daily: () => show(() => dailyDropScreen(ctx())),
    records: () => show(() => recordsScreen(ctx())),
    controls: () => show(() => controlsScreen(ctx())),
    cloud: () => show(() => cloudScreen(ctx())),
  };
}
